import React from "react";
import {
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	Row,
	Rule,
	stringToLocalDate,
	StyleProps,
	ViewableObserver,
} from "@hex-insights/core";
import {
	AcademicTermComponents,
	AnnouncementComponents,
	CalendarEventComponents,
	ContractComponents,
	NewsletterComponents,
	PaymentComponents,
	PostComponents,
	SchoolAttendanceRecordComponents,
	StudentMedicalEventComponents,
	StudentNoteComponents,
} from "../../Models/Components";
import {
	TimelineFilterInput,
	TimelineItem,
	TimelineObjectType,
	useGroupedTimelineItems,
	useTimelineData,
} from "../../Utilities";
import { EmptyView } from "../Empty";
import { Icon } from "../Icon";
import { TimelineViewerFilterSetsForm } from "./Filter";
import styles from "./styles.module.css";

const objectTypeWithDividers = {
	[TimelineObjectType.AcademicTerm]: true,
	[TimelineObjectType.Announcement]: true,
	[TimelineObjectType.CalendarEvent]: true,
	[TimelineObjectType.Contract]: false,
	[TimelineObjectType.Newsletter]: true,
	[TimelineObjectType.Payment]: false,
	[TimelineObjectType.Post]: true,
	[TimelineObjectType.SchoolAttendanceRecord]: false,
	[TimelineObjectType.StudentMedicalEvent]: false,
	[TimelineObjectType.StudentNote]: false,
};

export type TimelineProps = {
	filter?: TimelineFilterInput;
	pageSize?: number;
	modalVariant?: "default" | "admin";
} & Partial<ClassNameProps & StyleProps>;

export function Timeline({ filter, pageSize, modalVariant = "default", className, style }: TimelineProps) {
	const {
		loading,
		data,
		loadMore,
		loadingAcademicTerms,
		academicTermsMap,
		loadingAnnouncements,
		announcementsMap,
		loadingCalendarEvents,
		calendarEventsMap,
		loadingContracts,
		contractsMap,
		loadingNewsletters,
		newslettersMap,
		loadingPayments,
		paymentsMap,
		loadingPosts,
		postsMap,
		loadingSchoolAttendanceRecords,
		schoolAttendanceRecordsMap,
		loadingStudentMedicalEvents,
		studentMedicalEventsMap,
		loadingStudentNotes,
		studentNotesMap,
	} = useTimelineData({ filter, pageSize });
	const hasMore = data?.timeline.hasMore ?? true;
	const groups = useGroupedTimelineItems(data?.timeline.items);
	const loadingAny =
		loading ||
		loadingAnnouncements ||
		loadingCalendarEvents ||
		loadingContracts ||
		loadingNewsletters ||
		loadingPayments ||
		loadingPosts ||
		loadingSchoolAttendanceRecords ||
		loadingStudentMedicalEvents ||
		loadingStudentNotes;

	const noResults = !loadingAny && groups.length === 0;

	return (
		<Column justify="spaced-start" verticalSpacing="1.5rem" className={className} style={style}>
			<Conditional>
				<If condition={noResults}>
					<Timeline.Empty />
				</If>
				<Else>
					{groups.map((group, i) => (
						<React.Fragment key={i}>
							<If condition={i > 0}>
								<Rule thickness="2px" />
							</If>
							{group.heading && <Heading.H3 noMargin>{group.heading}</Heading.H3>}
							<Column justify="spaced-start" verticalSpacing="1rem" className={styles["timeline__group__body"]}>
								{group.items.map((e, i) => (
									<React.Fragment key={timelineItemKey(e)}>
										<If
											condition={
												i > 0 &&
												(objectTypeWithDividers[e.objectType] || objectTypeWithDividers[group.items[i - 1].objectType])
											}
										>
											<Rule thickness="2px" style={{ margin: "0.5rem 0" }} />
										</If>
										<Conditional>
											<If condition={e.objectType === TimelineObjectType.AcademicTerm}>
												<AcademicTermComponents.TimelineItem
													academicTerm={academicTermsMap[e.objectID] ?? null}
													isLoading={loadingAcademicTerms}
												/>
											</If>
											<If condition={e.objectType === TimelineObjectType.Announcement}>
												<AnnouncementComponents.TimelineItem
													announcement={announcementsMap[e.objectID] ?? null}
													isLoading={loadingAnnouncements}
												/>
											</If>
											<If condition={e.objectType === TimelineObjectType.CalendarEvent}>
												<CalendarEventComponents.TimelineItem
													calendarEvent={calendarEventsMap[e.objectID] ?? null}
													isLoading={loadingCalendarEvents}
												/>
											</If>
											<If condition={e.objectType === TimelineObjectType.Contract}>
												<ContractComponents.TimelineItem
													contract={contractsMap[e.objectID] ?? null}
													isLoading={loadingContracts}
												/>
											</If>
											<If condition={e.objectType === TimelineObjectType.Newsletter}>
												<NewsletterComponents.TimelineItem
													newsletter={newslettersMap[e.objectID] ?? null}
													isLoading={loadingNewsletters}
												/>
											</If>
											<If condition={e.objectType === TimelineObjectType.Payment}>
												<PaymentComponents.TimelineItem
													payment={paymentsMap[e.objectID] ?? null}
													isLoading={loadingPayments}
												/>
											</If>
											<If condition={e.objectType === TimelineObjectType.Post}>
												<PostComponents.TimelineItem
													post={postsMap[e.objectID] ?? null}
													isLoading={loadingPosts}
													modalVariant={modalVariant}
												/>
											</If>
											<If condition={e.objectType === TimelineObjectType.SchoolAttendanceRecord}>
												<SchoolAttendanceRecordComponents.TimelineItem
													schoolAttendanceRecord={schoolAttendanceRecordsMap[e.objectID] ?? null}
													isLoading={loadingSchoolAttendanceRecords}
												/>
											</If>
											<If condition={e.objectType === TimelineObjectType.StudentMedicalEvent}>
												<StudentMedicalEventComponents.TimelineItem
													studentMedicalEvent={studentMedicalEventsMap[e.objectID] ?? null}
													isLoading={loadingStudentMedicalEvents}
												/>
											</If>
											<If condition={e.objectType === TimelineObjectType.StudentNote}>
												<StudentNoteComponents.TimelineItem
													studentNote={studentNotesMap[e.objectID] ?? null}
													isLoading={loadingStudentNotes}
												/>
											</If>
											<Else>
												<Timeline.Item item={e} />
											</Else>
										</Conditional>
									</React.Fragment>
								))}
							</Column>
						</React.Fragment>
					))}
					<If condition={loading}>Loading...</If>
					<If condition={!loadingAny && hasMore && groups.length > 0}>
						<ViewableObserver onEnterView={loadMore} rootMargin="0px 0px 200px" rateLimitMS={1000} />
					</If>
				</Else>
			</Conditional>
		</Column>
	);
}

type TimelineItemProps = {
	item: Pick<TimelineItem, "objectType" | "objectID" | "objectTime">;
};

Timeline.Item = function ({ item }: TimelineItemProps) {
	return (
		<Column style={{ minHeight: "10rem" }}>
			<Row justify="spaced-start">
				<span>
					{item.objectType}: {item.objectID}
				</span>
				<span>{formatTimelineItemTime(item.objectTime)}</span>
			</Row>
		</Column>
	);
};

function formatTimelineItemTime(t: string) {
	const d = stringToLocalDate(t, "second");
	return d.toLocaleString();
}

function timelineItemKey(item: Pick<TimelineItem, "objectID" | "objectTime">) {
	return `${item.objectID}-${item.objectTime}`;
}

export {
	type TimelineViewerFilterSetsFormProps,
	type TimelineViewerFilterSetName,
	type TimelineViewerFilterSetsState,
	useTimelineViewerFilterSets,
} from "./Filter";

Timeline.ViewerFilterSetsForm = TimelineViewerFilterSetsForm;

Timeline.Empty = function () {
	return <EmptyView IconComponent={Icon.List} primaryText="Timeline" secondaryText="No activity to show" />;
};
